// configure axios create
import axios from "axios";
import Cookies from "js-cookie";
import { REFRESH_JWT_TOKEN } from "./api-urls";

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Get token from sessionStorage or cookie
const getToken = () => {
  const sessionData = sessionStorage.getItem("userInfo");
  if (sessionData) {
    try { return JSON.parse(sessionData).token; } catch { return null; }
  }
  const cookieData = Cookies.get("userToken");
  if (cookieData) {
    try { return JSON.parse(cookieData).token; } catch { return null; }
  }
  return null;
};

const getAuthorizationHeader = () => `Bearer ${getToken()}`;

// Authenticated JSON requests
const authApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Always set latest token before each request
authApi.interceptors.request.use(
  (config) => {
    config.headers.Authorization = getAuthorizationHeader();
    return config;
  },
  (error) => Promise.reject(error)
);

// Authenticated file upload requests
const authImageApi = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "multipart/form-data",
    type: "formData",
  },
});

authImageApi.interceptors.request.use(
  (config) => {
    config.headers.Authorization = getAuthorizationHeader();
    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Token Rotation Logic ────────────────────────────────────────────────────

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

const forceLogout = () => {
  sessionStorage.removeItem("userInfo");
  Cookies.remove("userToken");
  window.location.href = "/login";
};

const updateStoredToken = (newToken) => {
  // Update sessionStorage
  const sessionData = sessionStorage.getItem("userInfo");
  if (sessionData) {
    try {
      const parsed = JSON.parse(sessionData);
      parsed.token = newToken;
      sessionStorage.setItem("userInfo", JSON.stringify(parsed));
    } catch { /* ignore */ }
  }
  // Update cookie if it exists
  const cookieData = Cookies.get("userToken");
  if (cookieData) {
    try {
      const parsed = JSON.parse(cookieData);
      parsed.token = newToken;
      Cookies.set("userToken", JSON.stringify(parsed), { expires: 7 });
    } catch { /* ignore */ }
  }
};

const handleUnauthorized = async (error) => {
  const originalRequest = error.config;

  if (!error.response || error.response.status !== 401) {
    return Promise.reject(error);
  }

  const responseData = error.response.data;

  // Hard logout: this device was explicitly revoked or is a stale legacy session
  if (responseData?.data === "force_logout") {
    forceLogout();
    return Promise.reject(error);
  }

  // Prevent infinite loop if the refresh request itself fails
  if (originalRequest._retry) {
    forceLogout();
    return Promise.reject(error);
  }

  // Queue subsequent requests while refresh is in progress
  if (isRefreshing) {
    return new Promise((resolve, reject) => {
      failedQueue.push({ resolve, reject });
    })
      .then((token) => {
        originalRequest.headers.Authorization = `Bearer ${token}`;
        return axios(originalRequest);
      })
      .catch((err) => Promise.reject(err));
  }

  originalRequest._retry = true;
  isRefreshing = true;

  try {
    const { data } = await axios.post(
      `${process.env.REACT_APP_API_URL}${REFRESH_JWT_TOKEN}`,
      {},
      { headers: { Authorization: getAuthorizationHeader() } }
    );
    const newToken = data.token;
    updateStoredToken(newToken);
    processQueue(null, newToken);
    originalRequest.headers.Authorization = `Bearer ${newToken}`;
    return axios(originalRequest);
  } catch (refreshError) {
    processQueue(refreshError, null);
    forceLogout();
    return Promise.reject(refreshError);
  } finally {
    isRefreshing = false;
  }
};

// Attach interceptors to both auth clients
authApi.interceptors.response.use(
  (response) => response,
  handleUnauthorized
);

authImageApi.interceptors.response.use(
  (response) => response,
  handleUnauthorized
);

// For requests made with responseType: "blob", a JSON error response still
// arrives as a Blob rather than parsed JSON - read it to surface the real
// backend error message instead of a generic failure toast.
const getDownloadErrorMessage = async (error) => {
  const responseData = error?.response?.data;
  if (responseData instanceof Blob && responseData.type.includes("json")) {
    try {
      const parsed = JSON.parse(await responseData.text());
      if (parsed?.message) return parsed.message;
    } catch {
      // fall through to generic message
    }
  }
  return error?.message || "Failed to download report";
};

export {
  api,
  authApi,
  authImageApi,
  getAuthorizationHeader,
  getDownloadErrorMessage,
};

